import type { CartItem } from '@/shared/lib/db/schemas/cart.schema';
import type { ClubCatalogItem, ClubProgram } from '../types/club';

/** Pontos necessários para um valor em reais — mesma regra do backend (ceil). */
export function pointsForPrice(price: number, multiplier: number): number {
  if (!Number.isFinite(price) || !Number.isFinite(multiplier) || price <= 0 || multiplier <= 0) {
    return 0;
  }
  return Math.ceil(Math.round(price * multiplier * 1e6) / 1e6);
}

/** Pontos que um pedido de `value` reais rende, conforme o programa (floor). */
export function pointsEarnedFor(value: number, pointsPerReal: number): number {
  if (!Number.isFinite(value) || value <= 0 || pointsPerReal <= 0) return 0;
  return Math.floor(value * pointsPerReal);
}

export function formatPoints(points: number): string {
  const n = Math.max(0, Math.round(points || 0));
  return `${n.toLocaleString('pt-BR')} ${n === 1 ? 'ponto' : 'pontos'}`;
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number.isFinite(value) ? value : 0
  );
}

function formatRate(pointsPerReal: number): string {
  return Number.isInteger(pointsPerReal)
    ? String(pointsPerReal)
    : pointsPerReal.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

export interface HowItWorksStep {
  title: string;
  text: string;
}

/**
 * Cards "Como funciona", montados só com as regras configuradas no gestor:
 * quanto rende cada real, um exemplo de compra e quanto custa um produto.
 */
export function buildHowItWorks(program: ClubProgram, exampleProductPrice = 20): HowItWorksStep[] {
  const rate = program.points_per_real;
  const multiplier = program.product_multiplier;
  const examplePurchase = 50;
  const earned = pointsEarnedFor(examplePurchase, rate);
  const productPoints = pointsForPrice(exampleProductPrice, multiplier);
  const rateLabel = formatRate(rate);
  const pointsWord = rate === 1 ? 'ponto' : 'pontos';

  const steps: HowItWorksStep[] = [
    {
      title: 'Você compra',
      text: `A cada R$ 1 gasto você ganha ${rateLabel} ${pointsWord}.`,
    },
    {
      title: 'Acumula pontos',
      text: `Uma compra de ${formatCurrency(examplePurchase)} vira ${formatPoints(earned)}.`,
    },
    {
      title: 'Troca por produtos',
      text: `Um produto de ${formatCurrency(exampleProductPrice)} custa ${formatPoints(productPoints)}. Qualquer item do cardápio pode ser trocado.`,
    },
  ];

  if (!program.delivery_earns_points) {
    steps[1].text += ' A taxa de entrega não gera pontos.';
  }

  return steps;
}

export function expirationLabel(program: ClubProgram): string | null {
  if (program.expiration.type !== 'months' || !program.expiration.months) return null;
  const months = program.expiration.months;
  return `Os pontos valem por ${months} ${months === 1 ? 'mês' : 'meses'} depois de ganhos.`;
}

/** Soma dos pontos dos itens do carrinho resgatados pelo Clube. */
export function sumRedeemedPoints(items: Pick<CartItem, 'is_reward' | 'loyalty_points_cost'>[]) {
  return items.reduce(
    (acc, item) => (item.is_reward ? acc + Math.max(0, item.loyalty_points_cost || 0) : acc),
    0
  );
}

/**
 * Produtos do catálogo que o saldo paga, do mais caro em pontos ao mais barato:
 * o primeiro é o que a faixa do carrinho (PRATDEV-252) mostra.
 */
export function pickRedeemableItems<T extends Pick<ClubCatalogItem, 'points_required'>>(
  items: T[],
  spendable: number
): T[] {
  return items
    .filter(item => item.points_required > 0 && item.points_required <= spendable)
    .sort((a, b) => b.points_required - a.points_required);
}

/** Pontos de uma unidade da linha do carrinho, com complementos: mesma regra do backend. */
export function pointsForCartLine(
  item: Pick<CartItem, 'total_price' | 'quantity'>,
  multiplier: number
): number {
  return pointsForPrice(Number(item.total_price) / Math.max(1, item.quantity), multiplier);
}

/** Valor em reais dos itens resgatados (o que sai do total a pagar). */
export function sumRedeemedValue(items: Pick<CartItem, 'is_reward' | 'total_price'>[]) {
  return items.reduce(
    (acc, item) => (item.is_reward ? acc + Math.max(0, Number(item.total_price) || 0) : acc),
    0
  );
}

/** Data curta em pt-BR (ex.: 25/09/2026). */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Preço de vitrine de um produto do Clube (PRAT-153): o "a partir de" do
 * cardápio — base + complemento obrigatório mais barato. A API antiga não manda
 * `min_price`; aí vale o preço efetivo.
 */
export function catalogDisplayPrice(item: Pick<ClubCatalogItem, 'effective_price' | 'min_price'>) {
  return Number.isFinite(item.min_price) ? (item.min_price as number) : item.effective_price;
}
